<template>
  <div>
    <b-alert v-if="showReviewHint" show variant="info" class="mb-2">
      <div class="alert-body d-flex align-items-center" style="font-size: 13px">
        <feather-icon icon="CornerUpLeftIcon" class="mr-50" />
        <span>{{
          $t('Changes you make here will be applied to all shipments on the review screen')
        }}</span>
      </div>
    </b-alert>

    <div v-if="senderAddressMode === 'registered'">
      <b-alert
        v-if="hasLoadedRegisteredAddresses && !registeredAddressOptions.length"
        show
        variant="danger"
        class="mb-3 bulk-import-info-danger"
      >
        <div class="alert-body d-flex align-items-center" style="font-size: 13px">
          <feather-icon icon="InfoIcon" class="mr-50" />
          <span>{{ $t('Bulk Shipment Warning') }}</span>
        </div>
      </b-alert>

      <div
        v-if="selectedRegisteredAddress"
        class="registered-address-card mb-2"
        :class="{ 'registered-address-card--expanded': showRegisteredAddressSelector }"
      >
        <div class="d-flex flex-column flex-lg-row justify-content-between">
          <div class="d-flex">
            <div
              class="registered-address-card__icon d-flex align-items-center justify-content-center flex-shrink-0"
            >
              <feather-icon icon="HomeIcon" size="22" class="text-primary" />
            </div>
            <div class="ml-2">
              <div class="d-flex align-items-center flex-wrap">
                <h3 class="mb-0 font-weight-bolder">
                  {{ getRegisteredAddressTitle(selectedRegisteredAddress) }}
                </h3>
                <b-badge
                  v-if="isDefaultRegisteredAddress(selectedRegisteredAddress)"
                  variant="light-primary"
                  class="ml-1"
                >
                  {{ $t('Default') }}
                </b-badge>
              </div>
              <p class="registered-address-card__line mb-25">
                {{ getRegisteredAddressLine(selectedRegisteredAddress) }}
              </p>
              <p class="registered-address-card__line mb-25">
                {{ getRegisteredAddressLocation(selectedRegisteredAddress) }}
              </p>
              <p class="registered-address-card__line mb-0">
                {{ getRegisteredAddressPhone(selectedRegisteredAddress) }}
              </p>
            </div>
          </div>

          <div class="mt-2 mt-lg-0 flex-shrink-0 text-lg-right">
            <b-button
              variant="outline-primary"
              class="font-weight-bold"
              @click="$emit('toggle-registered-selector')"
            >
              {{ showRegisteredAddressSelector ? $t('Close') : $t('Change') }}
            </b-button>
          </div>
        </div>
      </div>

      <div
        v-if="showRegisteredAddressSelector && registeredAddressOptions.length"
        class="registered-address-list mb-3"
      >
        <button
          v-for="address in registeredAddressOptions"
          :key="address.id"
          type="button"
          class="registered-address-option text-left d-flex align-items-start"
          :class="{
            'registered-address-option--active': address.id === selectedRegisteredAddressId
          }"
          @click="$emit('select-registered-address', address.id)"
        >
          <div class="d-flex justify-content-between flex-column w-100">
            <div class="font-weight-bolder d-flex align-items-center flex-wrap">
              <div class="d-flex justify-content-between w-100">
                <h5>{{ getRegisteredAddressTitle(address) }}</h5>
                <feather-icon
                  v-if="address.id === selectedRegisteredAddressId"
                  icon="CheckCircleIcon"
                  class="text-primary"
                />
              </div>
              <b-badge
                v-if="isDefaultRegisteredAddress(address)"
                variant="light-primary"
                style="margin: 2px 0; padding: 6px"
              >
                {{ $t('Default') }}
              </b-badge>
            </div>

            <div>
              <div class="text-muted mt-25">{{ getRegisteredAddressLine(address) }}</div>
              <div class="text-muted">{{ getRegisteredAddressLocation(address) }}</div>
              <div class="text-muted">{{ getRegisteredAddressPhone(address) }}</div>
            </div>
          </div>
        </button>
      </div>
    </div>

    <validation-observer v-else ref="manualSenderAddressForm" tag="div" class="manual-sender-form">
      <div class="row">
        <div class="col-lg-6">
          <h3 class="manual-sender-form__section-title">{{ $t('Address Information') }}</h3>

          <validation-provider
            v-slot="{ errors }"
            :name="$t('Country')"
            rules="required"
            vid="manual_sender_country"
          >
            <b-form-group class="mb-2">
              <label class="font-weight-bolder mb-50">{{ $t('Country') }}</label>
              <div class="d-flex align-items-start sender-country-row">
                <div class="flex-grow-1">
                  <v-select
                    id="manual_sender_country"
                    v-model="manualSenderAddress.country"
                    :options="countries"
                    :searchable="true"
                    :clearable="false"
                    :loading="isLoadingCountries"
                    label="text"
                    :placeholder="isLoadingCountries ? $t('Loading') : $t('Make a Choice')"
                    @open="$emit('ensure-countries-loaded')"
                    @input="$emit('manual-country-change', $event)"
                  >
                    <span slot="no-options">
                      {{ isLoadingCountries ? `${$t('Loading')}...` : $t('No records found') }}
                    </span>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </div>
                <b-button
                  variant="info"
                  class="ml-1 sender-country-row__action"
                  @click="$emit('open-postal-code-search')"
                >
                  {{ $t('Address Search') }}
                </b-button>
              </div>
            </b-form-group>
          </validation-provider>

          <div class="row">
            <div class="col-md-6">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('City')"
                rules="required"
                vid="manual_sender_city"
              >
                <b-form-group class="mb-2">
                  <label class="font-weight-bolder mb-50">{{ $t('City') }}</label>
                  <b-form-input
                    v-model="manualSenderAddress.city"
                    :placeholder="$t('City')"
                    @input="$emit('manual-address-change')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </div>

            <div class="col-md-6">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('Postal Code')"
                rules="required"
                vid="manual_sender_postal_code"
              >
                <b-form-group class="mb-2">
                  <label class="font-weight-bolder mb-50">{{ $t('Postal Code') }}</label>
                  <b-form-input
                    v-model="manualSenderAddress.postal_code"
                    :placeholder="$t('Postal Code')"
                    @input="$emit('manual-address-change')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </div>
          </div>

          <validation-provider
            v-slot="{ errors }"
            :name="$t('District')"
            rules="required"
            vid="manual_sender_district"
          >
            <b-form-group class="mb-2">
              <label class="font-weight-bolder mb-50">{{ $t('District') }}</label>
              <b-form-input
                v-model="manualSenderAddress.district"
                :placeholder="$t('District')"
                @input="$emit('manual-address-change')"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <validation-provider
            v-slot="{ errors }"
            :name="$t('Address')"
            rules="required"
            vid="manual_sender_address"
          >
            <b-form-group class="mb-0">
              <label class="font-weight-bolder mb-50">{{ $t('Address') }}</label>
              <b-form-input
                v-model="manualSenderAddress.address"
                :placeholder="$t('Address')"
                @input="$emit('manual-address-change')"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>
        </div>

        <div class="col-lg-6 mt-3 mt-lg-0">
          <h3 class="manual-sender-form__section-title">
            {{ $t('Contact Information') }}
          </h3>

          <div class="row">
            <div class="col-md-6">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('Contact Person')"
                rules="required"
                vid="manual_sender_attention_name"
              >
                <b-form-group class="mb-2">
                  <label class="font-weight-bolder mb-50">{{ $t('Contact Person') }}</label>
                  <b-form-input
                    v-model="manualSenderAddress.attention_name"
                    :placeholder="$t('Contact Person')"
                    @input="$emit('manual-address-change')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </div>

            <div class="col-md-6">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('Company Name')"
                rules="required"
                vid="manual_sender_company_name"
              >
                <b-form-group class="mb-2">
                  <label class="font-weight-bolder mb-50">{{ $t('Company Name') }}</label>
                  <b-form-input
                    v-model="manualSenderAddress.company_name"
                    :placeholder="$t('Company Name')"
                    @input="$emit('manual-address-change')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('Phone Number')"
                rules="required"
                vid="manual_sender_phone"
              >
                <b-form-group class="mb-2">
                  <label class="font-weight-bolder mb-50">
                    {{ $t('Phone Number') }}
                  </label>
                  <b-form-input
                    v-model="manualSenderAddress.phone_number"
                    :placeholder="$t('Phone Number')"
                    @input="$emit('manual-address-change')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </div>

            <div class="col-md-6">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('Consignor E-Mail')"
                rules="required|email"
                vid="manual_sender_email"
              >
                <b-form-group class="mb-2">
                  <label class="font-weight-bolder mb-50">
                    {{ $t('Sender E-Mail') }}
                  </label>
                  <b-form-input
                    v-model="manualSenderAddress.email"
                    :placeholder="$t('Sender E-Mail')"
                    type="email"
                    @input="$emit('manual-address-change')"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </div>
          </div>

          <div class="sender-optional-divider"></div>

          <div class="row">
            <div class="col-md-6">
              <b-form-group class="mb-2">
                <label class="font-weight-bolder mb-50">
                  {{ $t('Tax Number (optional)') }}
                </label>
                <b-form-input
                  v-model="manualSenderAddress.tax_number"
                  :placeholder="$t('Tax Number')"
                  @input="$emit('manual-address-change')"
                />
              </b-form-group>
            </div>

            <div class="col-md-6">
              <b-form-group class="mb-2">
                <label class="font-weight-bolder mb-50">
                  {{ $t('EORI Number (optional)') }}
                </label>
                <b-form-input
                  v-model="manualSenderAddress.eori_number"
                  :placeholder="$t('EORI Number')"
                  @input="$emit('manual-address-change')"
                />
              </b-form-group>
            </div>

            <div class="col-md-6">
              <b-form-group class="mb-0">
                <label class="font-weight-bolder mb-50">
                  {{ $t('IOSS Number (optional)') }}
                </label>
                <b-form-input
                  v-model="manualSenderAddress.ioss_number"
                  :placeholder="$t('IOSS Number')"
                  @input="$emit('manual-address-change')"
                />
              </b-form-group>
            </div>
          </div>
        </div>
      </div>
    </validation-observer>

    <b-alert
      v-if="showDescriptionAlert"
      show
      variant="warning"
      class="mb-0 bulk-import-info-warning mt-2"
    >
      <div class="alert-body d-flex align-items-center" style="font-size: 13px">
        <feather-icon icon="InfoIcon" class="mr-50" />
        <span>
          {{ $t('Bulk Shipment Description') }}
        </span>
      </div>
    </b-alert>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import { BButton, BAlert, BBadge, BFormInput, BFormGroup } from 'bootstrap-vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';

export default {
  name: 'BulkShipmentSenderAddressEditor',
  components: {
    vSelect,
    BButton,
    BAlert,
    BBadge,
    BFormInput,
    BFormGroup,
    ValidationProvider,
    ValidationObserver
  },
  props: {
    senderAddressMode: {
      type: String,
      required: true
    },
    hasLoadedRegisteredAddresses: {
      type: Boolean,
      default: false
    },
    registeredAddressOptions: {
      type: Array,
      default: () => []
    },
    selectedRegisteredAddress: {
      type: Object,
      default: null
    },
    selectedRegisteredAddressId: {
      type: [String, Number],
      default: null
    },
    showRegisteredAddressSelector: {
      type: Boolean,
      default: false
    },
    countries: {
      type: Array,
      default: () => []
    },
    isLoadingCountries: {
      type: Boolean,
      default: false
    },
    manualSenderAddress: {
      type: Object,
      required: true
    },
    showReviewHint: {
      type: Boolean,
      default: false
    },
    showDescriptionAlert: {
      type: Boolean,
      default: true
    },
    isDefaultRegisteredAddress: {
      type: Function,
      required: true
    },
    getRegisteredAddressTitle: {
      type: Function,
      required: true
    },
    getRegisteredAddressLine: {
      type: Function,
      required: true
    },
    getRegisteredAddressLocation: {
      type: Function,
      required: true
    },
    getRegisteredAddressPhone: {
      type: Function,
      required: true
    }
  },
  methods: {
    async validateManualForm() {
      if (this.senderAddressMode !== 'manual') return true;
      return this.$refs.manualSenderAddressForm.validate();
    }
  }
};
</script>
